import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  Tooltip, Legend, XAxis, YAxis, CartesianGrid,
} from "recharts";

const API = process.env.REACT_APP_API_URL;
const COLORS = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
const inputCls = "border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white";

export default function Dashboard() {
  const { userInfo } = useSelector(state => state.userLogin || {});
  const [q, setQ] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [data, setData] = useState([]);
  const [bm, setBm] = useState({ rows: [], totalMerchants: 0, totalEnquiries: 0 });
  const [bmLoading, setBmLoading] = useState(false);
  const authConfig = {
    headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {},
  };

  const fetchData = async () => {
    const params = { q, page, limit: 10, ...(startDate && endDate ? { startDate, endDate } : {}) };
    const { data } = await axios.get(`${API}/report/enquiries`, { ...authConfig, params });
    setData(data.items || []);
    setPages(data.pages || 1);
  };

  const fetchByMerchant = async () => {
    setBmLoading(true);
    const params = { q, ...(startDate && endDate ? { startDate, endDate } : {}), limit: 50 };
    const { data } = await axios.get(`${API}/report/enquiries/by-merchant`, { ...authConfig, params });
    setBm(data || { rows: [], totalMerchants: 0, totalEnquiries: 0 });
    setBmLoading(false);
  };

  useEffect(() => {
    (async () => { await fetchData(); await fetchByMerchant(); })();
  }, [page]);

  const handleSearch = async e => {
    e.preventDefault();
    setPage(1);
    await fetchData();
    await fetchByMerchant();
  };

  const statusChartData = Object.entries(
    data.reduce((acc, cur) => {
      const key = cur.status || "Unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  ).map(([status, count]) => ({ status, count }));

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Loan Enquiry Dashboard</h1>
      </div>

      <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <input
          placeholder="Search case / mobile / status / remarks"
          value={q}
          onChange={e => setQ(e.target.value)}
          className={`${inputCls} flex-1 min-w-[220px]`}
        />
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inputCls} />
        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          Apply
        </button>
      </form>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {['#', 'Case Name', 'Merchant', 'Mobile Number', 'Loan Type', 'Status', 'Lead Date'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">No enquiries found</td>
                </tr>
              ) : data.map((row, i) => (
                <tr key={row._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-400">{i + 1 + (page - 1) * 10}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.caseName || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.merchant?.name || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.mobileNumber || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.loanType || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.status || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{row.leadDate ? new Date(row.leadDate).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <span className="text-sm text-gray-500">Page {page} of {Math.max(1, pages)}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
              Prev
            </button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= pages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
              Next
            </button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Enquiries by Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="status" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Enquiries Share</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusChartData} dataKey="count" nameKey="status" outerRadius={90} label>
                {statusChartData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Enquiries per Merchant</h3>
            <span className="text-xs text-gray-400">{bm.totalMerchants} merchants · {bm.totalEnquiries} enquiries</span>
          </div>
          {bmLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={bm.rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="merchantName" hide />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" name="Enquiries" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5 overflow-x-auto">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Top Merchants</h3>
          <table className="w-full">
            <thead>
              <tr>
                {['#', 'Merchant', 'Enquiries'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bm.rows.map((r, idx) => (
                <tr key={r.merchantId || `unlinked-${idx}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm text-gray-400">{idx + 1}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{r.merchantName}</td>
                  <td className="px-3 py-2 text-sm font-semibold text-gray-800">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
